import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Modal as AntModal } from "antd";
import { CloseIcon } from "../../../assets/icons";
import {
  Overlay,
  Modal,
  CloseButton,
  Header,
} from "../../../components/UploadButton/uploadButtonModals/SelectTemplateStyled";

const WideModal = styled(Modal)`
  width: 860px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  padding: 0 24px 16px;
  overflow-y: auto;
`;

const Summary = styled.div`
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 14px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 14px;
`;

const ActionButton = styled.button`
  padding: 7px 12px;
  border-radius: 7px;
  border: 1px solid ${(p) => (p.$danger ? "#fca5a5" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : p.$danger ? "#dc2626" : "#374151")};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const DocGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

const DocCard = styled.button`
  position: relative;
  text-align: left;
  border: 1px solid ${(p) => (p.$selected ? "#0C53BB" : "#e5e7eb")};
  background: ${(p) => (p.$selected ? "#eff6ff" : "#fff")};
  border-radius: 10px;
  min-height: 92px;
  padding: 16px 42px 16px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
`;

const Checkbox = styled.input`
  position: absolute;
  right: 10px;
  top: 10px;
  width: 18px;
  height: 18px;
  accent-color: #0C53BB;
`;

const DocName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  word-break: break-word;
`;

const DocMeta = styled.div`
  font-size: 11px;
  color: #6b7280;
`;

const ErrorList = styled.div`
  margin-top: 18px;
  padding: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  border-radius: 8px;
  color: #991b1b;
  font-size: 12px;
`;

const CombinePreview = styled.ol`
  margin: 10px 0 0;
  padding-left: 22px;
  max-height: 220px;
  overflow-y: auto;
  font-size: 13px;
`;

const RequiredInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  margin-top: 8px;
`;

const sortByDocumentName = (docs) =>
  [...docs].sort((a, b) =>
    (a?.name || "").localeCompare(b?.name || "", undefined, {
      numeric: false,
      sensitivity: "base",
    })
  );

export default function PackageResultsModal({
  result,
  open,
  onClose,
  onDownload,
  onDelete,
  onRename,
  onCombine,
  busy,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [combineOpen, setCombineOpen] = useState(false);
  const [finalPdfName, setFinalPdfName] = useState("");

  const documents = result?.documents || [];
  const errors = result?.errors || [];
  const selectedDocs = useMemo(
    () => documents.filter((doc) => selectedIds.includes(String(doc.id))),
    [documents, selectedIds]
  );
  const combineOrder = useMemo(() => sortByDocumentName(selectedDocs), [selectedDocs]);

  if (!open || !result) return null;

  const toggleDoc = (doc) => {
    const id = String(doc.id);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const clearSelection = () => setSelectedIds([]);

  const handleRename = async () => {
    if (selectedDocs.length !== 1) return;
    const current = selectedDocs[0];
    const nextName = window.prompt("New generated document name", current.name || "");
    if (!nextName || !nextName.trim() || nextName === current.name) return;
    await onRename(current, nextName.trim());
    clearSelection();
  };

  const handleDelete = async () => {
    if (!selectedDocs.length) return;
    if (!window.confirm(`Delete ${selectedDocs.length} generated document(s)?`)) return;
    await onDelete(selectedDocs);
    clearSelection();
  };

  const handleCombine = async () => {
    const trimmedName = finalPdfName.trim();
    if (!trimmedName) return;
    await onCombine(combineOrder, trimmedName);
    setCombineOpen(false);
    setFinalPdfName("");
    clearSelection();
  };

  return (
    <Overlay>
      <WideModal>
        <CloseButton onClick={onClose} aria-label="Close package results">
          <CloseIcon />
        </CloseButton>
        <Header>Package Generated</Header>
        <Body>
          <Summary>
            Select generated documents to download, delete, rename, or combine. The combine
            preview below sorts by generated document name.
          </Summary>

          <ActionBar>
            <ActionButton
              type="button"
              onClick={() => setSelectedIds(documents.map((doc) => String(doc.id)))}
              disabled={!documents.length || busy}
            >
              Select all
            </ActionButton>
            <ActionButton type="button" onClick={clearSelection} disabled={!selectedIds.length || busy}>
              Clear
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => selectedDocs.forEach(onDownload)}
              disabled={!selectedDocs.length || busy}
            >
              Download
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleRename}
              disabled={selectedDocs.length !== 1 || busy}
            >
              Edit name
            </ActionButton>
            <ActionButton
              type="button"
              $danger
              onClick={handleDelete}
              disabled={!selectedDocs.length || busy}
            >
              Delete
            </ActionButton>
            <ActionButton
              type="button"
              $primary
              onClick={() => setCombineOpen(true)}
              disabled={selectedDocs.length < 2 || busy}
            >
              Combine
            </ActionButton>
            <DocMeta>{selectedDocs.length} selected</DocMeta>
          </ActionBar>

          <DocGrid>
            {documents.map((doc) => {
              const selected = selectedIds.includes(String(doc.id));
              return (
                <DocCard
                  type="button"
                  key={doc.id}
                  $selected={selected}
                  onClick={() => toggleDoc(doc)}
                >
                  <Checkbox
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleDoc(doc)}
                    onClick={(event) => event.stopPropagation()}
                    aria-label={`Select ${doc.name}`}
                  />
                  <DocName>{doc.name}</DocName>
                </DocCard>
              );
            })}
          </DocGrid>

          {errors.length > 0 && (
            <ErrorList>
              <strong>Template errors:</strong>
              <ul>
                {errors.map((err, index) => (
                  <li key={`${err.template || "template"}-${index}`}>
                    {err.template || "Template"}: {err.error || "Unknown error"}
                  </li>
                ))}
              </ul>
            </ErrorList>
          )}

          <AntModal
            title="Combine selected documents"
            open={combineOpen}
            onOk={handleCombine}
            onCancel={() => setCombineOpen(false)}
            okText={busy ? "Combining..." : "Combine into PDF"}
            okButtonProps={{ disabled: !finalPdfName.trim() || busy }}
            cancelButtonProps={{ disabled: busy }}
            zIndex={2000}
          >
            <label>
              Final PDF name <span style={{ color: "#dc2626" }}>*</span>
              <RequiredInput
                value={finalPdfName}
                onChange={(event) => setFinalPdfName(event.target.value)}
                placeholder="e.g. Final VA Packet"
              />
            </label>
            <p style={{ marginTop: 14, marginBottom: 0 }}>
              DMS will combine these documents in alphabetical order:
            </p>
            <CombinePreview>
              {combineOrder.map((doc) => (
                <li key={doc.id}>{doc.name}</li>
              ))}
            </CombinePreview>
          </AntModal>
        </Body>
      </WideModal>
    </Overlay>
  );
}
